// @flow
import React, { Component, type Node } from 'react';
import DelayedSignupConfirmationDialog from 'app/common/react/components/growth/unauth/signup/GoogleOneTap/DelayedSignupConfirmationDialog';
import i18n from 'app/common/lib/i18n';
import LoadingMask from 'app/common/react/components/growth/unauth/signup/LoadingMask/LoadingMask';
import TermsOfService from 'app/common/react/components/growth/unauth/signup/TermsOfService/TermsOfService';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import { track } from 'app/common/lib/react/analytics';
import { Box, Button, IconButton, Text } from 'gestalt';

const styles = {
  modalOverlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    opacity: 1,
    position: 'fixed',
    top: '0px',
    bottom: '0px',
    left: '0px',
    right: '0px',
    zIndex: 1000,
  },
  staticContinueButtonLoginLink: {
    cursor: 'pointer',
    fontWeight: 'normal',
  },
};

export const CONFIRMATION_DIALOG_STEP = {
  DELAYED_SIGNUP_LOADING_SPINNER: 'delayedSignupLoadingSpinner',
  STATIC_SIGNUP_LOGIN: 'staticSignupLogin',
  STATIC_SIGNUP_LOGIN_WITH_TOS: 'staticSignupLoginWithTOS',
};

type Props = {|
  handleConfirmSignup: (boolean) => void,
  handleCancelClick: () => void,
  handleShowLogin: () => void,
  layoutStyle: 'top' | 'bottom',
  loading?: boolean,
  delayedSignupLoadingSpinnerDuration: number,
  step: $Values<typeof CONFIRMATION_DIALOG_STEP>,
  shouldHide?: boolean,
|};

export class GoogleOneTapModal extends Component<Props> {
  componentDidMount() {
    track(PCONST.thrift.EventType.RENDER, {
      viewParameter: PCONST.thrift.ViewParameterType.SIGNUP_GOOGLE_ONE_TAP_CONFIRMATION_DIALOG,
    });

    this.disableBackgroundScrolling();
    this.trackScopedAction('shown');
  }

  componentWillUnmount() {
    this.enableBackgroundScrolling();
  }

  trackScopedAction(action: string) {
    trackRegisterAction(`unauth.google_one_tap.confirmation_dialog.${this.props.step}.${action}`);
  }

  disableBackgroundScrolling() {
    // See Gestalt implementation: https://github.com/pinterest/gestalt/blob/master/src/Modal/Modal.js
    if (document.body) {
      document.body.style.overflow = 'hidden'; // Prevents background scrolling
    }
  }

  enableBackgroundScrolling() {
    if (document.body) {
      document.body.style.overflow = ''; // Reenables background scrolling
    }
  }

  handleCancelClick = () => {
    this.trackScopedAction('cancel.click');
    track(PCONST.thrift.EventType.CLICK, {
      element: PCONST.thrift.ElementType.CANCEL_BUTTON,
      view: PCONST.thrift.ViewType.REGISTRATION,
      viewParameter: PCONST.thrift.ViewParameterType.SIGNUP_GOOGLE_ONE_TAP_CONFIRMATION_DIALOG,
    });
    this.props.handleCancelClick();
  };

  handleConfirmSignup = () => {
    this.trackScopedAction('confirm.click');
    track(PCONST.thrift.EventType.CLICK, {
      element: PCONST.thrift.ElementType.SIGNUP_BUTTON,
      view: PCONST.thrift.ViewType.REGISTRATION,
      viewParameter: PCONST.thrift.ViewParameterType.SIGNUP_GOOGLE_ONE_TAP_CONFIRMATION_DIALOG,
    });
    this.props.handleConfirmSignup(false);
  };

  handleConfirmSignupWithSpinner = () => {
    this.trackScopedAction('confirm.click');
    track(PCONST.thrift.EventType.CLICK, {
      element: PCONST.thrift.ElementType.SIGNUP_BUTTON,
      view: PCONST.thrift.ViewType.REGISTRATION,
      viewParameter: PCONST.thrift.ViewParameterType.SIGNUP_GOOGLE_ONE_TAP_CONFIRMATION_DIALOG,
    });
    this.props.handleConfirmSignup(true);
  };

  handleShowLogin = () => {
    this.trackScopedAction('login.click');
    track(PCONST.thrift.EventType.CLICK, {
      element: PCONST.thrift.ElementType.LOGIN_BUTTON,
      view: PCONST.thrift.ViewType.REGISTRATION,
      viewParameter: PCONST.thrift.ViewParameterType.SIGNUP_GOOGLE_ONE_TAP_CONFIRMATION_DIALOG,
    });
    this.props.handleShowLogin();
  };

  // NB: These strings are effectively no-ops but will be picked up for translations while
  // the dependent feature code is being reviewed
  /* eslint-disable no-unused-vars */
  newStrings() {
    const staticConfirmationHeaderText = i18n._(
      'Create a new account',
      'Title of a dialog telling the user will create an account if they click the `continue` button below',
    );
    const staticConfirmationLoginLinkText = i18n._(
      'We’re almost done creating your Pinterest account—but if you already have one, it’s not too late to <b>log in here.</b>',
      'Clickable text which will stop a new account from being created and display the login form',
    );
    const staticConfirmationContinueButtonText = i18n._(
      'Continue',
      'Text on a button which will create an account when clicked',
    );

    const delayedSignupMidSignupHeaderText = i18n._(
      'Finishing up...',
      'Title of a dialog telling the user that an account is almost done being created.',
    );
    const delayedSignupPreSignupHeaderText = i18n._(
      'Creating your account...',
      'Title of a dialog telling the user that an account is being created.',
    );
    const delayedSignupSpinnerAccessibilitiyText = i18n._(
      'Account is being created',
      'Accessibility label for account creation loading dialog',
    );
    const delayedSignupUndoDesciriptionText = i18n._(
      'Didn’t mean to create a new account?',
      'Description text telling the user that they can log in if they did not intend to create a new account',
    );
    const delayedSignupLoginLinkText = i18n._(
      'Click here to log in.',
      'Login button which will show the login modal and enable the user to log in',
    );
  }
  /* eslint-enable no-unused-vars */

  renderDelayedSignupConfirmation(delay: number) {
    return (
      <DelayedSignupConfirmationDialog
        signupDelay={delay}
        handleLogin={this.handleShowLogin}
        handleSignup={() => {
          // disable the loading spinner in the confirmation dialog so the user can no longer attempt login
          this.handleConfirmSignup();
        }}
        loggingId={CONFIRMATION_DIALOG_STEP.DELAYED_SIGNUP_LOADING_SPINNER}
      />
    );
  }

  renderContentInModalContainer(Content: Node): Node {
    let Modal = null;
    if (this.props.layoutStyle === 'top') {
      Modal = (
        <Box
          color="white"
          direction="column"
          display="flex"
          margin={6}
          padding={7}
          position="fixed"
          right
          shape="rounded"
          top
          width={376}
        >
          {this.props.loading ? <LoadingMask /> : null}
          {Content}
        </Box>
      );
    } else {
      Modal = (
        <Box
          bottom
          color="white"
          direction="column"
          display="flex"
          height={168}
          padding={7}
          position="fixed"
          width="100%"
        >
          {this.props.loading ? <LoadingMask /> : null}
          {Content}
        </Box>
      );
    }

    return <div style={styles.modalOverlay}>{Modal}</div>;
  }

  renderStaticSignupLoginConfirmation(showTOS: boolean): Node {
    return (
      <Box>
        <Box display="inlineBlock" position="absolute" top right marginTop={2} marginRight={2}>
          <IconButton
            accessibilityLabel={i18n._('Cancel', 'Button to close the signup/login modal')}
            icon="cancel"
            onClick={this.handleCancelClick}
            size="sm"
          />
        </Box>
        <Text align="left" size="xl" bold>
          {i18n._(
            'Continue to Pinterest',
            'Title of a dialog telling enabling the user to continue with sign up or log in',
          )}
        </Text>
        <Box marginTop={2}>
          <Text align="left" size="md">
            {i18n._(
              "Hmm, it looks like you don't have an account yet. Let's get started!",
              'Description text of a dialog asking the user to sign up for a Pinterest account',
            )}
          </Text>
        </Box>
        <Box marginTop={5}>
          <Button
            color="red"
            onClick={this.handleConfirmSignupWithSpinner}
            text={i18n._('Sign up', 'Text on a button which will create an account when clicked')}
          />
        </Box>
        <Box marginTop={2}>
          <Button
            color="gray"
            onClick={this.handleShowLogin}
            text={i18n._(
              'Log in using a different method',
              'Text on a button which will take the user to login when clicked.',
            )}
          />
        </Box>
        {showTOS ? (
          <Box marginTop={4} marginLeft={4} marginRight={4}>
            <TermsOfService dark />
          </Box>
        ) : null}
      </Box>
    );
  }

  render() {
    if (this.props.shouldHide) {
      return null;
    }

    let Content = null;
    if (this.props.step === CONFIRMATION_DIALOG_STEP.DELAYED_SIGNUP_LOADING_SPINNER) {
      Content = this.renderDelayedSignupConfirmation(
        this.props.delayedSignupLoadingSpinnerDuration,
      );
    } else if (this.props.step === CONFIRMATION_DIALOG_STEP.STATIC_SIGNUP_LOGIN) {
      Content = this.renderStaticSignupLoginConfirmation(false);
    } else if (this.props.step === CONFIRMATION_DIALOG_STEP.STATIC_SIGNUP_LOGIN_WITH_TOS) {
      Content = this.renderStaticSignupLoginConfirmation(true);
    }

    return this.renderContentInModalContainer(Content);
  }
}

export default GoogleOneTapModal;
